import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type IOrderState } from '../../types/IOrderState';
import {
  fetchOrderByIdThunk,
  fetchOrderTicketsThunk,
  fetchOrderTrackingHistoryThunk,
  refreshOrderDataV2Thunk,
  changeOrderStatusAndRefreshThunk,
  changeOrderStatusThunk,
  updateOrderThunk,
  updateOrderOfferPriceThunk
} from './orderThunks';

const initialState: IOrderState = {
  // Основные данные заказа
  order: null,

  // Дополнительные данные
  trackingHistory: undefined,
  tickets: [],

  // Состояния загрузки
  isLoading: false,
  isConfirmationLoading: false,
  isUpdating: false,
  isTrackingHistoryLoading: false,
  isTicketsLoading: false,
  isOrderUpdating: false,

  //Глоб состояния
  isOrderOfferPriceUpdate: null,

  // Ошибки
  error: null,
  trackingHistoryError: null,
  ticketsError: null,
  updateError: null,

  // Действия
  pendingAction: null,
  actionError: null,

  // Служебные поля
  lastUpdated: null,
  currentOrderUuid: null
};

const order = createSlice({
  name: 'order',
  initialState,
  reducers: {
    resetOrder(state) {
      state.order = initialState.order;
      state.trackingHistory = initialState.trackingHistory;
      state.tickets = initialState.tickets;
      state.isLoading = initialState.isLoading;
      state.isConfirmationLoading = initialState.isConfirmationLoading;
      state.isUpdating = initialState.isUpdating;
      state.isTrackingHistoryLoading = initialState.isTrackingHistoryLoading;
      state.isTicketsLoading = initialState.isTicketsLoading;
      state.isOrderUpdating = initialState.isOrderUpdating;
      state.isOrderOfferPriceUpdate = initialState.isOrderOfferPriceUpdate;
      state.error = initialState.error;
      state.trackingHistoryError = initialState.trackingHistoryError;
      state.ticketsError = initialState.ticketsError;
      state.updateError = initialState.updateError;
      state.pendingAction = initialState.pendingAction;
      state.actionError = initialState.actionError;
      state.lastUpdated = initialState.lastUpdated;
      state.currentOrderUuid = initialState.currentOrderUuid;
    },

    setConfirmationLoading(state, action: PayloadAction<boolean>) {
      state.isConfirmationLoading = action.payload;
    },

    setCurrentOrderUuid(state, action: PayloadAction<string | null>) {
      if (state.currentOrderUuid !== action.payload) {
        state.order = null;
        state.trackingHistory = undefined;
        state.tickets = [];
        state.isLoading = false;
        state.isUpdating = false;
        state.isTrackingHistoryLoading = false;
        state.isTicketsLoading = false;
        state.error = null;
        state.trackingHistoryError = null;
        state.ticketsError = null;
        state.pendingAction = null;
        state.actionError = null;
        state.lastUpdated = null;
      }
      state.currentOrderUuid = action.payload;
    },

    clearOrderErrors(state) {
      state.error = null;
      state.trackingHistoryError = null;
      state.ticketsError = null;
      state.actionError = null;
      state.updateError = null;
    },

    clearUpdateError(state) {
      state.updateError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderByIdThunk.pending, (state, action) => {
        if (state.currentOrderUuid === action.meta.arg.uuid) {
          state.isLoading = true;
          state.error = null;
        }
      })
      .addCase(fetchOrderByIdThunk.fulfilled, (state, action) => {
        if (state.currentOrderUuid === action.meta.arg.uuid) {
          state.order = action.payload;
          state.isLoading = false;
          state.error = null;
          state.lastUpdated = new Date().toISOString();
        }
      })
      .addCase(fetchOrderByIdThunk.rejected, (state, action) => {
        if (state.currentOrderUuid === action.meta.arg.uuid) {
          state.isLoading = false;
          state.error = action.payload ?? {
            message: 'Ошибка при загрузке заказа'
          };
        }
      });

    builder
      .addCase(fetchOrderTicketsThunk.pending, (state, action) => {
        if (state.currentOrderUuid === action.meta.arg.orderUuid) {
          state.isTicketsLoading = true;
          state.ticketsError = null;
        }
      })
      .addCase(fetchOrderTicketsThunk.fulfilled, (state, action) => {
        if (state.currentOrderUuid === action.meta.arg.orderUuid) {
          state.tickets = action.payload;
          state.isTicketsLoading = false;
          state.ticketsError = null;
        }
      })
      .addCase(fetchOrderTicketsThunk.rejected, (state, action) => {
        if (state.currentOrderUuid === action.meta.arg.orderUuid) {
          state.isTicketsLoading = false;
          state.ticketsError = action.payload ?? {
            message: 'Ошибка при загрузке тикетов'
          };
        }
      });

    builder
      .addCase(fetchOrderTrackingHistoryThunk.pending, (state, action) => {
        if (state.currentOrderUuid === action.meta.arg.orderUuid) {
          state.isTrackingHistoryLoading = true;
          state.trackingHistoryError = null;
        }
      })
      .addCase(fetchOrderTrackingHistoryThunk.fulfilled, (state, action) => {
        if (state.currentOrderUuid === action.meta.arg.orderUuid) {
          state.trackingHistory = action.payload;
          state.isTrackingHistoryLoading = false;
          state.trackingHistoryError = null;
        }
      })
      .addCase(fetchOrderTrackingHistoryThunk.rejected, (state, action) => {
        if (state.currentOrderUuid === action.meta.arg.orderUuid) {
          state.isTrackingHistoryLoading = false;
          state.trackingHistoryError = action.payload ?? {
            message: 'Ошибка при загрузке истории статусов доставки'
          };
        }
      });

    builder
      .addCase(refreshOrderDataV2Thunk.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.trackingHistoryError = null;
        state.ticketsError = null;
      })
      .addCase(refreshOrderDataV2Thunk.fulfilled, (state, action) => {
        state.order = action.payload.order;
        state.trackingHistory = action.payload.trackingHistory;
        state.tickets = action.payload.tickets;
        state.isUpdating = false;
        state.error = null;
        state.trackingHistoryError = null;
        state.ticketsError = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(refreshOrderDataV2Thunk.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? {
          message: 'Ошибка при обновлении данных заказа'
        };
      });

    builder
      .addCase(changeOrderStatusAndRefreshThunk.pending, (state, action) => {
        state.pendingAction = action.meta.arg.action;
        state.actionError = null;
      })
      .addCase(changeOrderStatusAndRefreshThunk.fulfilled, (state) => {
        state.pendingAction = null;
      })
      .addCase(changeOrderStatusAndRefreshThunk.rejected, (state, action) => {
        state.actionError = action.payload ?? {
          message: 'Ошибка при смене статуса'
        };
        state.pendingAction = null;
      });
    builder
      .addCase(changeOrderStatusThunk.pending, (state, action) => {
        state.pendingAction = action.meta.arg.action;
        state.actionError = null;
      })
      .addCase(changeOrderStatusThunk.fulfilled, (state) => {
        state.pendingAction = null;
      })
      .addCase(changeOrderStatusThunk.rejected, (state, action) => {
        state.actionError = action.payload ?? {
          message: 'Ошибка при смене статуса'
        };
        state.pendingAction = null;
      });

    builder
      .addCase(updateOrderThunk.pending, (state) => {
        state.isOrderUpdating = true;
        state.updateError = null;
      })
      .addCase(updateOrderThunk.fulfilled, (state, action) => {
        state.order = action.payload;
        state.isOrderUpdating = false;
        state.updateError = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateOrderThunk.rejected, (state, action) => {
        state.isOrderUpdating = false;
        state.updateError = action.payload ?? {
          message: 'Ошибка при обновлении заказа'
        };
      });

    builder
      .addCase(updateOrderOfferPriceThunk.pending, (state, action) => {
        state.isOrderOfferPriceUpdate = {
          orderOfferUuid: String(action.meta.arg.orderOfferId),
          isActive: true
        };
        state.updateError = null;
      })
      .addCase(updateOrderOfferPriceThunk.fulfilled, (state, action) => {
        state.order = action.payload;
        state.isOrderOfferPriceUpdate = null;
        state.updateError = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateOrderOfferPriceThunk.rejected, (state, action) => {
        state.isOrderOfferPriceUpdate = null;
        state.updateError = action.payload ?? {
          message: 'Ошибка при обновлении цены товара'
        };
      });
  }
});

export const {
  resetOrder,
  setConfirmationLoading,
  setCurrentOrderUuid,
  clearOrderErrors,
  clearUpdateError
} = order.actions;

export default order.reducer;
